import React from 'react';
import {
  Alert,
  Button,
  Checkbox,
  Input,
  Modal,
  Radio,
  Select,
  Tag,
  Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import CustomButtonBase from 'components/CustomButton';
import CustomInputBase from 'components/CustomInput';

export const CustomButton = CustomButtonBase;

export const CustomInput = CustomInputBase;

export const CustomTextArea = ({ className = '', ...props }) => (
  <Input.TextArea className={className} {...props} />
);

export const CustomCheckbox = (props) => <Checkbox {...props} />;

export const CustomTag = ({ name, children, ...props }) => (
  <Tag {...props}>{name || children}</Tag>
);

export const CustomModal = ({ shown, visible, ...props }) => (
  <Modal visible={typeof shown === 'boolean' ? shown : visible} {...props} />
);

export const CustomSelect = ({
  options,
  selected,
  value,
  onSelect,
  children,
  ...props
}) => {
  const selectValue = typeof value === 'undefined' ? selected : value;

  if (children) {
    return (
      <Select value={selectValue} onSelect={onSelect} {...props}>
        {children}
      </Select>
    );
  }

  return (
    <Select value={selectValue} onSelect={onSelect} {...props}>
      {(options || []).map(({ slug, name, value: optionValue }) => {
        const resolvedValue = optionValue ?? slug ?? name;
        return (
          <Select.Option key={resolvedValue} value={resolvedValue}>
            {name || slug || resolvedValue}
          </Select.Option>
        );
      })}
    </Select>
  );
};

export const CustomToggle = ({ options = [], checked, onChange, disabled }) => (
  <Radio.Group
    disabled={disabled}
    value={checked ? options[0] : options[1]}
    onChange={(event) => onChange(event.target.value === options[0])}
  >
    {options.map((option) => (
      <Radio.Button key={option} value={option}>
        {option}
      </Radio.Button>
    ))}
  </Radio.Group>
);

export const CustomAddressSearch = ({
  placeholder,
  addressValue,
  latitude,
  longitude,
  onChangeLocation,
}) => (
  <Input
    placeholder={placeholder}
    value={addressValue}
    onChange={(event) =>
      onChangeLocation({
        text: event.target.value,
        latitude,
        longitude,
      })
    }
  />
);

export const CustomMultiSelect = ({
  options = [],
  selected,
  onSelect,
  disabledOptions = [],
  disabled = false,
  title,
}) => (
  <Select
    mode="multiple"
    disabled={disabled}
    value={selected || []}
    placeholder={title}
    onChange={onSelect}
    style={{ width: '100%' }}
  >
    {options.map(({ slug, name }) => (
      <Select.Option
        key={slug || name}
        value={slug || name}
        disabled={disabledOptions.includes(slug || name)}
      >
        {name || slug}
      </Select.Option>
    ))}
  </Select>
);

const POLICY_KEYS = [
  'minLength',
  'maxLength',
  'hasSymbols',
  'hasUppercase',
  'hasLowercase',
  'hasNumbers',
  'forbiddenWords',
  'forbidEmail',
];

export const PasswordPolicyInfo = ({ policy, intl }) => {
  const rules = POLICY_KEYS.filter((key) => {
    const value = policy?.[key];
    if (typeof value === 'boolean') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return typeof value !== 'undefined' && value !== null;
  });

  return (
    <Tooltip
      title={
        rules.length
          ? (
            <div>
              {rules.map((key) => (
                <div key={key}>
                  {intl.formatMessage({ id: `password_policy_${key}` }, { [key]: policy?.[key] })}
                </div>
              ))}
            </div>
            )
          : null
      }
    >
      <InfoCircleOutlined style={{ marginLeft: 8 }} />
    </Tooltip>
  );
};

export const ErrorBoundaryFallback = ({
  titleErrorMessage,
  descriptionErrorMessage,
  buttonLabel,
  customErrorImage,
}) => (
  <div style={{ padding: 24, textAlign: 'center' }}>
    {customErrorImage ? (
      <img
        src={customErrorImage}
        alt="error"
        style={{ maxWidth: 240, marginBottom: 16 }}
      />
    ) : null}
    <Alert
      type="error"
      showIcon
      message={titleErrorMessage}
      description={descriptionErrorMessage}
      style={{ textAlign: 'left', marginBottom: 16 }}
    />
    <Button type="primary" onClick={() => window.location.reload()}>
      {buttonLabel}
    </Button>
  </div>
);
